import React, { useState, useCallback } from 'react';
import {
  useSearchClientDocumentsQuery,
  useGetClientUploadDownloadUrlMutation,
  useGetMigratedDocumentUrlMutation,
  useSetClientUploadYearMutation,
  useGetAdminConfigQuery,
} from '../../app/api';
import { useIsMobile, fetchAsBlobUrl } from '../utils';
import DocumentBrowser from '../../components/DocumentBrowser';
import DocumentViewerModal from '../../components/DocumentViewerModal';
import useRoleJSON from './useRoleJSON';

const work = {
  deepBlue: '#0a2947', tealBlue: '#0069a6', goldenrod: '#d09b2c', ink: '#0a2947',
  muted: '#6b7280', hairline: '#dde2e8', surface: '#ffffff', canvas: '#f3f5f8',
};

// Employee-side cross-client document search. Scope (internal-firm + unassigned
// clients) is enforced server-side by /clients/documents — a B2B firm's client
// documents never appear here. All search/filter/facet UI lives in the shared
// DocumentBrowser component.
export default function Documents() {
  const isMobile = useIsMobile();
  const [params, setParams] = useState({ sort: 'recent' });
  const roleJSON = useRoleJSON();

  const { data, isFetching, isError, error, refetch } = useSearchClientDocumentsQuery(params);
  const [getDownloadUrl] = useGetClientUploadDownloadUrlMutation();
  const [getMigratedUrl] = useGetMigratedDocumentUrlMutation();
  const [setUploadYear] = useSetClientUploadYearMutation();
  const { data: adminConfig } = useGetAdminConfigQuery();
  const showDownloadButtons = adminConfig?.config?.show_download_buttons !== false;
  // In-app viewer state — {url: blob:..., filename} or null. Rendered via
  // DocumentViewerModal, an <iframe> over the current page instead of a new
  // browser tab, so viewing a document never leaves the authenticated app.
  const [viewer, setViewer] = useState(null);
  const closeViewer = useCallback(() => {
    setViewer(v => {
      if (v?.url) URL.revokeObjectURL(v.url);
      return null;
    });
  }, []);

  const onParamsChange = useCallback((patch) => {
    setParams((prev) => ({ ...prev, ...patch }));
  }, []);

  // Both handlers take the whole document. Two corpora share this list now:
  // a portal upload (upload_id, one-time download-url endpoint) and a
  // Salesforce document (doc_id, /api/documents/{doc_id} behind its own
  // one-time link). Same two-step in each case - mint a link, fetch it into a
  // blob - so only the minting call differs.
  const onDownload = useCallback(async (doc) => {
    try {
      const { url, filename } = doc.source_system === 'fsc'
        ? { ...(await getMigratedUrl({ docId: doc.doc_id }).unwrap()), filename: doc.original_filename }
        : await getDownloadUrl({ upload_id: doc.upload_id }).unwrap();
      if (!url) return;
      // Fetch once ourselves and hand the browser a blob: URL — see
      // fetchAsBlobUrl's comment for why we never navigate to the one-time
      // link directly. A hidden <a download> reliably forces a save dialog
      // for a blob regardless of content type.
      const blobUrl = await fetchAsBlobUrl(url);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename || 'document';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch {
      window.alert('Could not generate a download link for that file.');
    }
  }, [getDownloadUrl, getMigratedUrl]);

  // "View" shows the file in an in-app modal (DocumentViewerModal), vs.
  // "Download" which forces a save. Fetching the one-time link ourselves and
  // handing the iframe a blob: URL — rather than a new browser tab pointed at
  // a real URL — keeps the document behind the app's own session for as long
  // as it's on screen; see fetchAsBlobUrl's comment for the underlying reason
  // we never navigate anywhere to the one-time link directly.
  const onView = useCallback(async (doc) => {
    try {
      const { url, filename } = doc.source_system === 'fsc'
        ? { ...(await getMigratedUrl({ docId: doc.doc_id }).unwrap()), filename: doc.original_filename }
        : await getDownloadUrl({ upload_id: doc.upload_id, disposition: 'inline' }).unwrap();
      if (!url) throw new Error('No URL returned.');
      const blobUrl = await fetchAsBlobUrl(url);
      setViewer({ url: blobUrl, filename });
    } catch {
      window.alert('Could not open that file.');
    }
  }, [getDownloadUrl, getMigratedUrl]);

  // The client's name links to their Portal Clients page — only for people
  // who hold that area too, or the link would bounce them back out of Work.
  const canOpenClients = Boolean(roleJSON?.isWorkPortalClients);
  const clientHref = useCallback(
    (publicId) => (publicId && canOpenClients ? `/work/clients/${publicId}` : null),
    [canOpenClients],
  );

  const onSetYear = useCallback(async (uploadId, documentYear) => {
    try {
      await setUploadYear({ upload_id: uploadId, document_year: documentYear }).unwrap();
      refetch();
    } catch {
      window.alert('Could not save that year — please try again.');
    }
  }, [setUploadYear, refetch]);

  return (
    <div style={{ background: work.canvas, minHeight: '100vh', padding: isMobile ? '0.75rem' : '1.5rem' }}>
      {/* Header card — matches the Work palette */}
      <div style={{ background: work.surface, border: `1px solid ${work.hairline}`, borderTop: `3px solid ${work.deepBlue}`, borderRadius: 6, padding: isMobile ? '1rem' : '1.25rem 1.5rem', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <div style={{ width: 44, height: 44, borderRadius: 8, background: work.deepBlue, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <i className="bi bi-folder2-open" style={{ color: work.goldenrod, fontSize: '1.4rem' }} />
        </div>
        <div>
          <p style={{ color: work.muted, fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '1.5px', margin: '0 0 2px' }}>
            Wealth Management
          </p>
          <h3 style={{ color: work.deepBlue, fontWeight: 800, margin: 0 }}>Client Documents</h3>
          <p style={{ color: work.muted, margin: '2px 0 0', fontSize: '0.85rem' }}>
            Search and browse every document your clients have uploaded, plus what is already on file in Salesforce for the clients linked to a Salesforce client.
          </p>
        </div>
      </div>

      <DocumentBrowser
        documents={data?.documents || []}
        facets={data?.facets || {}}
        total={data?.total || 0}
        filtered={data?.filtered || 0}
        loading={isFetching}
        error={isError ? (error?.data?.detail || 'Could not load documents.') : null}
        params={params}
        onParamsChange={onParamsChange}
        onDownload={showDownloadButtons ? onDownload : undefined}
        onView={onView}
        clientHref={clientHref}
        showFirmFilter
        showAdvisorFilter
        showYearFilter
        onSetYear={onSetYear}
      />

      <DocumentViewerModal url={viewer?.url} filename={viewer?.filename} onClose={closeViewer} />
    </div>
  );
}
